"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { MatchupHeader } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { formatDateDayMonth, formatTimeHHMM } from "@/utilities/formatting/date";
import type { HighlightedMatch } from "../utils/pickHighlightedMatches";
export interface HighlightedMatchesProps {
  matches: HighlightedMatch[];
}

/**
 * HighlightedMatches - Displays 2 highlighted upcoming matches per competition.
 *
 * Shows the most interesting upcoming matches based on standings data,
 * with badges indicating why they're interesting (Title Race, Relegation Battle, etc.).
 *
 * Must be wrapped in NavigationProvider by parent component.
 */
export function HighlightedMatches(t0) {
  const $ = _c(11);
  const {
    matches
  } = t0;
  const nav = useNavigation();
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setMounted(true);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (matches.length === 0) {
    return null;
  }
  let t3;
  if ($[2] !== matches || $[3] !== mounted || $[4] !== nav) {
    let t4;
    if ($[6] !== mounted || $[7] !== nav) {
      t4 = match => {
        const kickoffDate = new Date(match.timing.kickoffTime);
        const matchId = match.fansUnitedMatchId || match.id;
        const dateStr = mounted ? formatDateDayMonth(kickoffDate) : "\xA0";
        const timeStr = mounted ? formatTimeHHMM(kickoffDate) : "\xA0";
        return <Link key={matchId} href={nav.match(matchId)} prefetch={false} className="block"><MatchupHeader centerContent={<div className="text-center mx-1"><div className="text-xs text-muted-foreground font-medium">{dateStr}</div><div className="text-xs text-muted-foreground">{timeStr}</div></div>} leftEntity={{
            image: <TeamLogo src={match.competitorOne.assets?.logo} alt={match.competitorOne.name} size="md" countryCode={match.competitorOne.country?.code} countryFlag={match.competitorOne.country?.flag} primaryColor={match.competitorOne.metadata?.branding?.primaryColor} secondaryColor={match.competitorOne.metadata?.branding?.secondaryColor} />,
            imageUrl: match.competitorOne.assets?.logo || "",
            name: match.competitorOne.name,
            styles: {
              imageBorderRadius: "md",
              imageSize: "md",
              typography: {
                name: "text-sm font-semibold text-foreground leading-tight",
                subtitle: "text-xs text-muted-foreground"
              }
            }
          }} rightEntity={{
            image: <TeamLogo src={match.competitorTwo.assets?.logo} alt={match.competitorTwo.name} size="md" countryCode={match.competitorTwo.country?.code} countryFlag={match.competitorTwo.country?.flag} primaryColor={match.competitorTwo.metadata?.branding?.primaryColor} secondaryColor={match.competitorTwo.metadata?.branding?.secondaryColor} />,
            imageUrl: match.competitorTwo.assets?.logo || "",
            name: match.competitorTwo.name,
            styles: {
              imageBorderRadius: "md",
              imageSize: "md",
              typography: {
                name: "text-sm font-semibold text-foreground leading-tight",
                subtitle: "text-xs text-muted-foreground"
              }
            }
          }} styles={{
            background: "bg-card px-3 py-2 rounded-md hover:bg-muted transition-colors border border-border min-h-16",
            centerTypography: "text-xs text-muted-foreground",
            gap: "2"
          }} variant="inline" /></Link>;
      };
      $[6] = mounted;
      $[7] = nav;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = matches.map(t4);
    $[2] = matches;
    $[3] = mounted;
    $[4] = nav;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <div className="space-y-2">{t3}</div>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}